/**
 * FloatingPanel — shared shell component for all floating, draggable panels.
 *
 * Provides:
 *  - Position: fixed positioning driven by controlled x/y props, with
 *    smooth drag-to-move on the header bar; calls onMove on drag end so the
 *    parent can persist the new position.
 *  - Resize: optional south / east / south-east handles that grow the panel
 *    via local state (size is not persisted — resets on remount).
 *  - Cursor: uses the app's custom .cur files via process.env.PUBLIC_URL so
 *    webpack never tries to bundle them with url().
 *
 * Consumers provide their own header content and body content; this component
 * only manages the structural shell and interactive behaviours.
 */
import React, {
    useRef,
    useEffect,
    useState,
    useCallback,
    forwardRef,
    useImperativeHandle,
} from 'react';

// ─── Public types ─────────────────────────────────────────────────────────────

export interface FloatingPanelProps {
    /** Current position (controlled). FloatingPanel manages live updates during drag;
     *  calls onMove with the final position on mouseup so the parent can persist it. */
    x: number;
    y: number;
    onMove: (pos: { x: number; y: number }) => void;

    /** Starting width in px. */
    defaultWidth: number;
    /** Starting height in px; omit for auto (content-driven) height. */
    defaultHeight?: number;
    /** Minimum width enforced during east-axis resize (default: 180). */
    minWidth?: number;
    /** Minimum height enforced during south-axis resize (default: 120). */
    minHeight?: number;

    /** Which resize handles to render. */
    resizeEdges?: Array<'s' | 'e' | 'se'>;

    /** Content rendered inside the header bar (which is the drag target). */
    header: React.ReactNode;
    /** Content rendered inside the scrollable body. */
    children: React.ReactNode;

    /** Added to the outer .fp shell alongside the base class. */
    className?: string;
    /** Added to the .fp__header element (use for per-panel colour tints etc.). */
    headerClassName?: string;
    /** Passed through to the outer shell (e.g. onClick e.stopPropagation). */
    onClick?: React.MouseEventHandler<HTMLDivElement>;
    /** Extra inline styles applied to the outer shell (last, so they override).
     *  Useful for forcing height:'auto' when a consumer is in minimised state. */
    style?: React.CSSProperties;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Render a floating, draggable panel shell.
 * The ref is forwarded to the outermost div so consumers can query its children.
 */
const FloatingPanel = forwardRef<HTMLDivElement, FloatingPanelProps>(
    function FloatingPanel(
        {
            x,
            y,
            onMove,
            defaultWidth,
            defaultHeight,
            minWidth = 180,
            minHeight = 120,
            resizeEdges = [],
            header,
            children,
            className = '',
            headerClassName = '',
            onClick,
            style,
        },
        ref,
    ) {
        const innerRef = useRef<HTMLDivElement>(null);
        // Expose the inner div through the forwarded ref
        useImperativeHandle(ref, () => innerRef.current!);

        // ── Cursor URLs (resolved at runtime so webpack doesn't bundle .cur files) ──
        const grabCursor = `url(${process.env.PUBLIC_URL || ''}/hand-cursor.cur) 8 8, grab`;
        const grabbingCursor = `url(${process.env.PUBLIC_URL || ''}/hand-pinch-cursor.cur) 16 16, grabbing`;

        // ── Position — live during drag, synced from props otherwise ──────────────
        // Clamp so a stale/persisted position from a wider window can never push the
        // panel fully off-screen (leaving at least 40px of its left/top edge visible).
        const clampPos = useCallback(
            (p: { x: number; y: number }) => ({
                x: Math.min(p.x, Math.max(0, window.innerWidth - 40)),
                y: Math.min(p.y, Math.max(0, window.innerHeight - 40)),
            }),
            [],
        );

        const [pos, setPos] = useState(() => clampPos({ x, y }));

        // Sync when the parent changes position (e.g. project switch or store reset)
        useEffect(() => {
            setPos(clampPos({ x, y }));
        }, [x, y, clampPos]);

        // ── Panel size (local — not persisted; resets on remount) ─────────────────
        const [size, setSize] = useState<{ width: number; height: number | undefined }>({
            width: defaultWidth,
            height: defaultHeight,
        });

        // ── Dragging ──────────────────────────────────────────────────────────────
        const [dragging, setDragging] = useState<{
            startMouse: { x: number; y: number };
            startPos: { x: number; y: number };
        } | null>(null);

        const startDrag = useCallback(
            (e: React.MouseEvent<HTMLDivElement>) => {
                // Let button clicks inside the header pass through without starting a drag
                if ((e.target as HTMLElement).closest('button')) return;
                e.preventDefault();
                setDragging({ startMouse: { x: e.clientX, y: e.clientY }, startPos: { ...pos } });
            },
            [pos],
        );

        useEffect(() => {
            if (!dragging) return;

            const onMouseMove = (e: globalThis.MouseEvent) => {
                setPos({
                    x: Math.max(0, dragging.startPos.x + (e.clientX - dragging.startMouse.x)),
                    y: Math.max(0, dragging.startPos.y + (e.clientY - dragging.startMouse.y)),
                });
            };

            const onMouseUp = (e: globalThis.MouseEvent) => {
                const nx = Math.max(0, dragging.startPos.x + (e.clientX - dragging.startMouse.x));
                const ny = Math.max(0, dragging.startPos.y + (e.clientY - dragging.startMouse.y));
                setPos({ x: nx, y: ny });
                onMove({ x: nx, y: ny });
                setDragging(null);
            };

            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
            return () => {
                window.removeEventListener('mousemove', onMouseMove);
                window.removeEventListener('mouseup', onMouseUp);
            };
            // onMove is a Zustand action — stable reference. Intentionally omitted from deps.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [dragging]);

        // ── Resizing ──────────────────────────────────────────────────────────────
        const [resizing, setResizing] = useState<{
            edge: 's' | 'e' | 'se';
            startMouse: { x: number; y: number };
            startSize: { width: number; height: number };
        } | null>(null);

        /**
         * Initiates a resize gesture, capturing the panel's current rendered
         * dimensions as the baseline so the resize starts from what the user sees.
         */
        const startResize = (edge: 's' | 'e' | 'se') => (e: React.MouseEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            const el = innerRef.current;
            setResizing({
                edge,
                startMouse: { x: e.clientX, y: e.clientY },
                startSize: {
                    width: el?.offsetWidth ?? defaultWidth,
                    height: el?.offsetHeight ?? defaultHeight ?? 400,
                },
            });
        };

        useEffect(() => {
            if (!resizing) return;

            const onMouseMove = (e: globalThis.MouseEvent) => {
                const dw = e.clientX - resizing.startMouse.x;
                const dh = e.clientY - resizing.startMouse.y;
                setSize((prev) => ({
                    // Width changes on east and south-east; stays fixed on pure south
                    width:
                        resizing.edge !== 's'
                            ? Math.max(minWidth, resizing.startSize.width + dw)
                            : prev.width,
                    // Height changes on south and south-east; stays as-is on pure east
                    height:
                        resizing.edge !== 'e'
                            ? Math.max(minHeight, resizing.startSize.height + dh)
                            : prev.height,
                }));
            };

            const onMouseUp = () => setResizing(null);

            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
            return () => {
                window.removeEventListener('mousemove', onMouseMove);
                window.removeEventListener('mouseup', onMouseUp);
            };
        }, [resizing, minWidth, minHeight]);

        // ── Render ────────────────────────────────────────────────────────────────
        return (
            <div
                ref={innerRef}
                className={`fp floating-panel ${className}`}
                style={{
                    '--fp-left': `${pos.x}px`,
                    '--fp-top': `${pos.y}px`,
                    '--fp-width': `${size.width}px`,
                    // Only apply explicit height when it has been set (either defaultHeight
                    // or via south-axis resize); otherwise let the panel be content-driven.
                    ...(size.height != null ? { '--fp-height': `${size.height}px` } : {}),
                    // Any overrides from the consumer (e.g. { height: 'auto' } when minimised)
                    // go last so they win over the computed values above.
                    ...style,
                } as React.CSSProperties}
                onClick={onClick}
            >
                {/* ── Header — drag target ── */}
                <div
                    className={`fp__header ${headerClassName}${dragging ? ' fp__header--dragging' : ''}`}
                    onMouseDown={startDrag}
                    style={{ cursor: dragging ? grabbingCursor : grabCursor }}
                >
                    {header}
                </div>

                {/* ── Body — scrollable content area ── */}
                <div className='fp__body'>{children}</div>

                {/* ── Resize handles ── */}
                {resizeEdges.includes('s') && (
                    <div className='fp__resize-s' onMouseDown={startResize('s')} />
                )}
                {resizeEdges.includes('e') && (
                    <div className='fp__resize-e' onMouseDown={startResize('e')} />
                )}
                {resizeEdges.includes('se') && (
                    <div className='fp__resize-se' onMouseDown={startResize('se')} />
                )}
            </div>
        );
    },
);

export default FloatingPanel;
